import { Flex, Modal, Typography } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { UserRoles } from "~enums/userRoles.ts";
import UserPermissionArrayValueLabel from "~pages/Users/components/UserPermissionArrayValueLabel/UserPermissionArrayValueLabel.tsx";
import type { UserPermissionTransformed } from "~pages/Users/types";
import type { PermissionScopeResources } from "~types/permissionScope.ts";
import ScopeView from "./ScopeView";
import extractScopeData from "./utilities/extractScopeData";

import "./UserPermissionsModal.scss";

const additionalPermissionSettingsUserRoles = [UserRoles.EntityGroupAdmin, UserRoles.EntityGroupManager, UserRoles.EntityManager];

type Props = {
  userPermission: UserPermissionTransformed | null;
  permissionScopeResources: PermissionScopeResources;
  isOpen: boolean;
  onClose: () => void;
};

export const UserPermissionModal = ({ userPermission, permissionScopeResources, isOpen, onClose }: Props) => {
  const { formatMessage } = useIntl();

  if (!userPermission) {
    return null;
  }

  const showAdditionalPermissions = !additionalPermissionSettingsUserRoles.includes(userPermission.userRole as UserRoles);
  const scopeOptions = extractScopeData(userPermission, permissionScopeResources).sort((a, b) =>
    a.sortValue.localeCompare(b.sortValue, undefined, { sensitivity: "base" }),
  );

  const userRoleName = formatMessage({ id: `permissions.userRole.${userPermission.userRole}` });
  const scopeName = userPermission.scope ? formatMessage({ id: userPermission.scope.toLowerCase() }) : "";

  let title = userRoleName;
  if (scopeName) {
    title += ` - ${scopeName}`;
  }

  const { seasonStatuses = null, fixtureStatuses = null } = userPermission;

  return (
    <Modal
      className="permissions-modal permissions-modal-view-user-permission"
      open={isOpen}
      width={750}
      centered
      onCancel={onClose}
      title={formatMessage({ id: "permissions.user.role.viewAccess" })}
      cancelText={formatMessage({ id: "close" })}
      okButtonProps={{ hidden: true }}
      destroyOnHidden
    >
      <Flex justify="space-between" align="center">
        <Typography.Text className="section-header">{title}</Typography.Text>
        {userPermission.scope && scopeOptions.length > 0 && (
          <Typography.Text className="items-selected-text">
            <FormattedMessage
              id="items.selected"
              values={{
                number: scopeOptions.length,
                itemType: formatMessage({
                  id: scopeOptions.length > 1 ? userPermission.scope.toLowerCase() : userPermission.scope.slice(0, -1).toLowerCase(),
                }),
              }}
            />
          </Typography.Text>
        )}
      </Flex>
      <Flex gap={8} vertical>
        <ScopeView scopeType={userPermission.scope} options={scopeOptions} key={userPermission.permissionId} />
        {showAdditionalPermissions && (
          <>
            <div>
              <Typography.Text className="section-header">{formatMessage({ id: "season.statuses" })}</Typography.Text>
              <br />
              <UserPermissionArrayValueLabel values={seasonStatuses} />
            </div>
            <div>
              <Typography.Text className="section-header">{formatMessage({ id: "fixture.statuses" })}</Typography.Text>
              <br />
              <UserPermissionArrayValueLabel values={fixtureStatuses} />
            </div>
          </>
        )}
      </Flex>
    </Modal>
  );
};
